import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Alert,
  Snackbar,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Autocomplete,
  CircularProgress,
  Divider,
  Grid,
} from '@mui/material';
import { useTheme } from '@emotion/react';
import { tokens } from '../../theme';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
import Headeer from '../../components/Headeer';
import FormulaService, {
  FORMULA_TEMPLATES,
} from '../../services/doctorServices/FormulaService';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FunctionsIcon from '@mui/icons-material/Functions';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import LightbulbIcon from '@mui/icons-material/Lightbulb';

// Debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

/** Şablon seçiliyken dil değişince açıklama alanını güncelle */
function TemplateDescriptionLanguageSync({ selectedFormula, templates, setFieldValue }) {
  const { i18n } = useTranslation("formulas");
  useEffect(() => {
    if (!selectedFormula) return;
    const tpl = templates.find((x) => x.formula === selectedFormula);
    if (tpl) setFieldValue("description", tpl.description);
  }, [i18n.language, selectedFormula, templates, setFieldValue]);
  return null;
}

const CreateFormula = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t, i18n } = useTranslation('formulas');
  const navigate = useNavigate();
  const formulaService = new FormulaService();

  const [testResult, setTestResult] = useState(null);
  const [testLoading, setTestLoading] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  /** Seçilen hazır şablonun formula ifadesi (dil değişince açıklamayı yenilemek için) */
  const [selectedTemplateFormula, setSelectedTemplateFormula] = useState(null);

  // Validation Schema
  const getValidationSchema = () => Yup.object().shape({
    name: Yup.string()
      .required(t('formulaNameRequiredValidation'))
      .min(3, t('formulaNameMinLength'))
      .max(100, t('formulaNameMaxLength')),
    description: Yup.string()
      .max(500, t('descriptionMaxLength')),
    formula_expression: Yup.string()
      .required(t('formulaExpressionRequiredValidation'))
      .min(3, t('formulaExpressionMinLength')),
  });

  const initialValues = {
    name: '',
    description: '',
    formula_expression: '',
    required_categories: [],
    required_keys: [],
  };

  // Test formula with debounce
  const testFormula = async (expression, categories, keys) => {
    if (!expression || expression.trim() === '') {
      setTestResult(null);
      return;
    }

    // Frontend validation first
    const validation = formulaService.validateFormulaExpression(expression);
    if (!validation.valid) {
      setTestResult({
        valid: false,
        error: validation.error,
      });
      return;
    }

    setTestLoading(true);
    try {
      const result = await formulaService.testFormula(
        expression,
        categories || [],
        keys || []
      );
      setTestResult(result);
    } catch (error) {
      setTestResult({
        valid: false,
        error: error.response?.data?.error || t('errorDuringTest'),
      });
    } finally {
      setTestLoading(false);
    }
  };

  // Template seçimi
  const handleTemplateSelect = (template, setFieldValue) => {
    if (template) {
      setFieldValue('name', template.name || '');
      setFieldValue('description', template.description || '');
      setFieldValue('formula_expression', template.formula || '');
      setFieldValue('required_categories', []);
      setFieldValue('required_keys', []);
      setSelectedTemplateFormula(template.formula || null);
      setCurrentFormula(template.formula || '');
      testFormula(template.formula, [], []);
    } else {
      setSelectedTemplateFormula(null);
    }
  };

  // Variable ekleme
  const handleInsertVariable = (variable, currentValue, setFieldValue) => {
    const newValue = currentValue ? `${currentValue} ${variable}` : variable;
    setFieldValue('formula_expression', newValue);
    setCurrentFormula(newValue); // Update tracking state
  };

  // Form submit
  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      // is_public her zaman false olsun (private formül)
      const formulaData = {
        ...values,
        is_public: false,
      };
      
      const response = await formulaService.createFormula(formulaData);
      
      setSnackbarMessage(t('formulaCreatedSuccessfully'));
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
      
      // Redirect to list page after 2 seconds
      setTimeout(() => {
        navigate('/formulas');
      }, 2000);
      
    } catch (error) {
      // 403 (Permission) errors are handled by global interceptor
      if (error.response?.status !== 403) {
        const errorMsg = error.response?.data?.error || t('errorCreatingFormula');
        setSnackbarMessage(`❌ ${errorMsg}`);
        setSnackbarSeverity('error');
        setOpenSnackbar(true);
      }
      // For 403 errors, global alert will be shown automatically
    } finally {
      setSubmitting(false);
    }
  };

  /** Şablon açıklamaları: descriptionKey varsa i18n (formulas namespace) */
  const templates = useMemo(
    () =>
      FORMULA_TEMPLATES.map((tpl) => {
        const { descriptionKey, ...rest } = tpl;
        return {
          ...rest,
          description: descriptionKey ? t(descriptionKey) : tpl.description,
        };
      }),
    [t, i18n.language]
  );
  const variables = useMemo(() => {
    const raw = formulaService.getAvailableVariables();
    return {
      ...raw,
      questions: raw.questions.map((v) => {
        const { descKey, ...rest } = v;
        return descKey ? { ...rest, desc: t(descKey) } : v;
      }),
    };
  }, [t, i18n.language]);

  // State for current form values (to track outside Formik)
  const [currentFormula, setCurrentFormula] = useState('');

  // Debounced formula - MUST be at component level
  const debouncedFormula = useDebounce(currentFormula, 800);

  // Test formula when debounced value changes - MUST be at component level
  useEffect(() => {
    if (debouncedFormula) {
      testFormula(debouncedFormula, [], []);
    }
  }, [debouncedFormula]);

  return (
    <Box m="20px">
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Headeer
          title={t('createScoringFormula')}
          subtitle={t('createScoringFormulaSubtitle')}
        />
      </Box>

      <Formik
        initialValues={initialValues}
        validationSchema={getValidationSchema()}
        onSubmit={handleSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
          isSubmitting,
        }) => {
          // Custom change handler that also updates tracking state
          const handleFormulaChange = (e) => {
            handleChange(e);
            setCurrentFormula(e.target.value);
          };

          return (
            <form onSubmit={handleSubmit}>
              <TemplateDescriptionLanguageSync
                selectedFormula={selectedTemplateFormula}
                templates={templates}
                setFieldValue={setFieldValue}
              />
              <Grid container spacing={3}>
                {/* Left Column - Form */}
                <Grid item xs={12} md={8}>
                  <Paper
                    sx={{
                      p: 3,
                      backgroundColor: colors.primary[400],
                      borderRadius: '12px',
                      boxShadow: `0 4px 24px rgba(0, 0, 0, 0.15)`,
                    }}
                  >
                    {/* Template Selection */}
                    <Box mb={3}>
                      <Typography
                        variant="h6"
                        sx={{
                          mb: 2,
                          color: colors.grey[100],
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <LightbulbIcon sx={{ color: colors.greenAccent[400] }} />
                        {t('startFromTemplate')}
                      </Typography>
                      <Autocomplete
                        value={
                          templates.find((o) => o.formula === selectedTemplateFormula) ??
                          null
                        }
                        options={templates}
                        openOnFocus
                        ListboxProps={{ style: { maxHeight: 320 } }}
                        getOptionLabel={(option) => option?.name ?? ''}
                        isOptionEqualToValue={(option, value) =>
                          option?.formula === value?.formula
                        }
                        onChange={(e, value) => handleTemplateSelect(value, setFieldValue)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder={t('selectTemplateOrStart')}
                            variant="outlined"
                          />
                        )}
                        renderOption={(props, option) => (
                          <Box component="li" {...props}>
                            <Box sx={{ py: 0.25 }}>
                              <Typography variant="body1" fontWeight={600}>
                                {option.name}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                                sx={{
                                  display: "block",
                                  whiteSpace: "normal",
                                  lineHeight: 1.4,
                                  mt: 0.25,
                                }}
                              >
                                {option.description}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                      />
                    </Box>

                    <Divider sx={{ mb: 3, backgroundColor: colors.grey[700] }} />

                    {/* Formula Name */}
                    <TextField
                      fullWidth
                      label={t('formulaNameRequired')}
                      name="name"
                      value={values.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                      placeholder={t('formulaNamePlaceholder')}
                      sx={{ mb: 3 }}
                    />

                    {/* Description */}
                    <TextField
                      fullWidth
                      label={t('descriptionLabel')}
                      name="description"
                      value={values.description}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.description && Boolean(errors.description)}
                      helperText={touched.description && errors.description}
                      placeholder={t('descriptionPlaceholder')}
                      multiline
                      rows={2}
                      sx={{ mb: 3 }}
                    />

                    {/* Formula Expression */}
                    <Box mb={3}>
                      <TextField
                        fullWidth
                        label={t('formulaExpressionRequired')}
                        name="formula_expression"
                        value={values.formula_expression}
                        onChange={handleFormulaChange}
                        onBlur={handleBlur}
                        error={touched.formula_expression && Boolean(errors.formula_expression)}
                        helperText={
                          touched.formula_expression && errors.formula_expression
                            ? errors.formula_expression
                            : t('formulaExpressionHelper')
                        }
                        placeholder={t('formulaExpressionPlaceholder')}
                        multiline
                        rows={4}
                        sx={{
                          '& textarea': {
                            fontFamily: 'monospace',
                            fontSize: '0.95rem',
                          },
                        }}
                      />

                      {/* Test Result */}
                      {testLoading && (
                        <Box mt={2} display="flex" alignItems="center" gap={1}>
                          <CircularProgress size={20} />
                          <Typography variant="body2" color={colors.grey[400]}>
                            {t('testing')}
                          </Typography>
                        </Box>
                      )}

                      {testResult && !testLoading && (
                        <Alert
                          severity={testResult.valid ? 'success' : 'error'}
                          icon={testResult.valid ? <CheckCircleIcon /> : <ErrorIcon />}
                          sx={{ mt: 2 }}
                        >
                          {testResult.valid ? (
                            <Box>
                              <Typography variant="body2" fontWeight={600}>
                                {t('formulaIsValid')}
                              </Typography>
                              <Typography variant="caption" display="block" mt={1}>
                                {t('testScore')} <strong>{testResult.testScore}</strong>
                              </Typography>
                              <Typography
                                variant="caption"
                                display="block"
                                sx={{ fontFamily: 'monospace', mt: 0.5 }}
                              >
                                {t('calculated')} {testResult.testExpression}
                              </Typography>
                            </Box>
                          ) : (
                            <Box>
                              <Typography variant="body2" fontWeight={600}>
                                {t('formulaIsInvalid')}
                              </Typography>
                              <Typography variant="caption" display="block" mt={1}>
                                {testResult.error}
                              </Typography>
                            </Box>
                          )}
                        </Alert>
                      )}
                    </Box>


                    {/* Action Buttons */}
                    <Box display="flex" gap={2} mt={4}>
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={isSubmitting || !testResult?.valid}
                        startIcon={isSubmitting ? <CircularProgress size={20} /> : <SaveIcon />}
                        sx={{
                          backgroundColor: colors.greenAccent[600],
                          color: colors.grey[100],
                          fontSize: '14px',
                          fontWeight: 'bold',
                          padding: '12px 32px',
                          borderRadius: '8px',
                          '&:hover': {
                            backgroundColor: colors.greenAccent[700],
                          },
                          '&:disabled': {
                            backgroundColor: colors.grey[700],
                          },
                        }}
                      >
                        {isSubmitting ? t('creating') : t('saveFormula')}
                      </Button>

                      <Button
                        variant="outlined"
                        onClick={() => navigate('/formulas')}
                        startIcon={<CancelIcon />}
                        sx={{
                          color: colors.grey[300],
                          borderColor: colors.grey[600],
                          '&:hover': {
                            borderColor: colors.grey[400],
                          },
                        }}
                      >
                        {t('cancel')}
                      </Button>
                    </Box>
                  </Paper>
                </Grid>

                {/* Right Column - Helper */}
                <Grid item xs={12} md={4}>
                  {/* Available Variables */}
                  <Paper
                    sx={{
                      p: 2,
                      mb: 2,
                      backgroundColor: colors.primary[400],
                      borderRadius: '12px',
                      maxHeight: '85vh',
                      overflowY: 'auto',
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 2,
                        color: colors.grey[100],
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <FunctionsIcon sx={{ color: colors.blueAccent[400] }} />
                      {t('availableVariables')}
                    </Typography>

                    {/* Global Variables */}
                    <Accordion
                      defaultExpanded
                      sx={{
                        backgroundColor: colors.primary[500],
                        mb: 1,
                      }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography fontWeight={600}>{t('globalVariables')}</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        {variables.global.map((v, index) => (
                          <Box key={index} mb={1}>
                            <Chip
                              label={v.var}
                              size="small"
                              onClick={() =>
                                handleInsertVariable(
                                  v.example,
                                  values.formula_expression,
                                  setFieldValue
                                )
                              }
                              icon={<ContentCopyIcon fontSize="small" />}
                              sx={{
                                backgroundColor: `${colors.blueAccent[600]}30`,
                                color: colors.blueAccent[300],
                                cursor: 'pointer',
                                '&:hover': {
                                  backgroundColor: `${colors.blueAccent[600]}50`,
                                },
                              }}
                            />
                            <Typography variant="caption" display="block" ml={1} mt={0.5}>
                              {v.desc}
                            </Typography>
                          </Box>
                        ))}
                      </AccordionDetails>
                    </Accordion>

                    {/* Question Variables */}
                    <Accordion sx={{ backgroundColor: colors.primary[500], mb: 1 }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography fontWeight={600}>{t('questionVariables')}</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        {variables.questions.map((v, index) => (
                          <Box key={index} mb={1.5}>
                            <Chip
                              label={v.var}
                              size="small"
                              onClick={() =>
                                handleInsertVariable(
                                  v.example,
                                  values.formula_expression,
                                  setFieldValue
                                )
                              }
                              icon={<ContentCopyIcon fontSize="small" />}
                              sx={{
                                backgroundColor: `${colors.greenAccent[600]}30`,
                                color: colors.greenAccent[300],
                                cursor: 'pointer',
                                '&:hover': {
                                  backgroundColor: `${colors.greenAccent[600]}50`,
                                },
                              }}
                            />
                            <Typography
                              variant="body2"
                              display="block"
                              ml={1}
                              mt={0.75}
                              sx={{
                                color: colors.grey[300],
                                fontSize: "0.8rem",
                                lineHeight: 1.5,
                                whiteSpace: "normal",
                              }}
                            >
                              {v.desc}
                            </Typography>
                          </Box>
                        ))}
                      </AccordionDetails>
                    </Accordion>

                    {/* Operators */}
                    <Accordion sx={{ backgroundColor: colors.primary[500] }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography fontWeight={600}>{t('operators')}</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        {variables.operators.map((v, index) => (
                          <Box key={index} mb={1}>
                            <Chip
                              label={v.var}
                              size="small"
                              sx={{
                                backgroundColor: `${colors.grey[600]}30`,
                                color: colors.grey[300],
                              }}
                            />
                            <Typography variant="caption" display="block" ml={1} mt={0.5}>
                              {v.desc}
                            </Typography>
                          </Box>
                        ))}
                      </AccordionDetails>
                    </Accordion>
                  </Paper>
                </Grid>
              </Grid>
            </form>
          );
        }}
      </Formik>

      {/* Snackbar */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CreateFormula;

 